import React, { Component } from 'react';
import { Drawer, Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { DialogWrapper } from 'components/DialogWrapper';
import PrescreenAnswers from 'containers/Manage-Questionnaire/prescreen/PrescreenAnswers';
import {
  DrawerHeader,
  FlexRow,
  BlueTextPointer,
  PlainButton,
  CategoryLabel,
  BorderWrapper,
} from 'components/styled-components';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import Select from 'react-select';
import FormTextAreaGroup from 'components/forms/components/form-text-area-group';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import {
  updatePrescreenQuestion,
  updatePrescreenDraftQuestion,
  archivePrescreenQuestion,
} from 'api/prescreen';
import { AppToaster } from 'components/AppToaster';
import {
  HeaderControls,
  PageNumbers,
  Remove,
  ScrollWrapper,
} from 'containers/Manage-Questionnaire/styled-components';
import { statusByVersionStatus } from 'utils/functions';

const mapAnswerLimit = val => {
  if (typeof val === 'number') {
    return val === 0
      ? { label: 'No Limit', value: 0 }
      : { label: val, value: val };
  } else {
    return val;
  }
};

class PrescreenEditQuestionDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editMade: false,
      question: cloneDeep(this.props.question),
      removeQuestion: false,
      closeWithoutSaving: false,
      questionError: false,
    };
  }

  componentDidUpdate(prevProps, _) {
    if (!isEqual(prevProps.question, this.props.question)) {
      this.setState({ question: cloneDeep(this.props.question) });
    }
  }

  handleCloseDrawer = () => {
    if (this.state.editMade) {
      this.toggleCloseWithoutSaving();
    } else {
      this.resetComponent();
      this.props.closeDrawer();
      this.props.setPrescreenIndex(null);
    }
  };

  resetComponent = () =>
    this.setState({
      question: cloneDeep(this.props.question),
      editMade: false,
      questionError: false,
    });

  handlePageChange = nextPage => {
    this.props.closeDrawer();
    this.props.setPrescreenIndex(nextPage);
  };

  toggleEditMade = question => {
    if (isEqual(question, this.props.question)) {
      this.setState({ editMade: false });
    } else {
      this.setState({ editMade: true });
    }
  };

  handleTextEdit = ({ target }) => {
    const { value } = target;
    const { question } = this.state;
    question.question = value;
    this.setState({ question });
    this.toggleEditMade(question);
    this.validateQuestionValue(value);
  };

  validateQuestionValue = questionValue => {
    if (questionValue.length > 0) {
      this.setState({ questionError: false });
    } else {
      this.setState({ questionError: true });
    }
  };

  handleAnswerLimitChange = answerLimit => {
    const { question } = this.state;
    question.answerLimit = answerLimit;
    this.toggleEditMade(question);
    this.setState({ question });
  };

  handleDismissRemoveQuestion = () => this.setState({ removeQuestion: false });
  toggleCloseWithoutSaving = () =>
    this.setState({ closeWithoutSaving: !this.state.closeWithoutSaving });

  handleArchiveQuestion = () => {
    const { questionID, versionStatus } = this.state.question;
    archivePrescreenQuestion(questionID, versionStatus)
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.resetComponent();
        this.props.closeDrawer();
        this.props.fetchPrescreenQuestions();
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleUpdateQuestion = () => {
    const { question } = this.state;
    if (question.versionStatus === 'Active') {
      updatePrescreenQuestion(question)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchPrescreenQuestions();
          this.setState({ editMade: false }, this.handleCloseDrawer);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    } else {
      updatePrescreenDraftQuestion(question)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchPrescreenQuestions();
          this.setState({ editMade: false }, this.handleCloseDrawer);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    }
  };

  render() {
    const { question } = this.state;
    const { index, total, currentIndex, isOpen, mapping } = this.props;
    const status = statusByVersionStatus(question.versionStatus);

    return (
      <Drawer
        isOpen={isOpen || isEqual(currentIndex, index)}
        onClose={this.handleCloseDrawer}
      >
        <DrawerHeader>
          Edit Question
          {this.state.editMade ? (
            <FlexRow fontSize="0.7rem" width="15rem">
              <BlueTextPointer onClick={this.toggleCloseWithoutSaving}>
                DISCARD
              </BlueTextPointer>
              <PlainButton
                onClick={this.handleUpdateQuestion}
                disabled={this.state.questionError}
              >
                SAVE CHANGES
              </PlainButton>
            </FlexRow>
          ) : (
            <HeaderControls>
              <PageNumbers>
                {mapping ? index.index + 1 : index + 1} of {total}
              </PageNumbers>
              <Button
                icon={IconNames.CHEVRON_LEFT}
                onClick={
                  mapping
                    ? () =>
                        this.handlePageChange({
                          ...index,
                          index: index.index - 1,
                        })
                    : () => this.handlePageChange(currentIndex - 1)
                }
                disabled={mapping ? index.index === 0 : index === 0}
                minimal
              />
              <Button
                icon={IconNames.CHEVRON_RIGHT}
                onClick={
                  mapping
                    ? () =>
                        this.handlePageChange({
                          ...index,
                          index: index.index + 1,
                        })
                    : () => this.handlePageChange(currentIndex + 1)
                }
                disabled={
                  mapping ? index.index === total - 1 : index === total - 1
                }
                minimal
              />
              <Button
                icon={IconNames.CROSS}
                onClick={this.handleCloseDrawer}
                minimal
              />
            </HeaderControls>
          )}
        </DrawerHeader>
        <ScrollWrapper>
          {this.state.questionError && (
            <ErrorWrapper>The question field cannot be blank.</ErrorWrapper>
          )}
          <FormTextAreaGroup
            label="Text"
            requiredLabel={false}
            name="question"
            fill={true}
            value={question.question}
            handleChange={this.handleTextEdit}
            style={{ background: colors.formBackground }}
          />
          <BorderWrapper>
            <PrescreenAnswers
              answers={question.answers}
              openParent={this.props.openParent}
              isParentOpen={this.props.isParentOpen}
              question={question}
              fetchPrescreenQuestions={this.props.fetchPrescreenQuestions}
              categories={this.props.categories}
            />
          </BorderWrapper>
          <CategoryLabel>Answer Limit</CategoryLabel>

          <Select
            onChange={this.handleAnswerLimitChange}
            value={mapAnswerLimit(question.answerLimit)}
            options={map(
              new Array(question.answers && question.answers.length),
              (a, i) => {
                if (i === 0) {
                  return { label: 'No Limit', value: i };
                } else {
                  return {
                    label: i,
                    value: i,
                  };
                }
              }
            )}
          />

          <Remove onClick={() => this.setState({ removeQuestion: true })}>
            {status.getTitle}
          </Remove>

          <DialogWrapper
            isOpen={this.state.removeQuestion}
            onClose={this.handleDismissRemoveQuestion}
            title={status.getTitle}
            body={status.getBody}
            confirm={status.getConfirm}
            action={this.handleArchiveQuestion}
            actionArgs={this.state.question.questionID}
          />

          <DialogWrapper
            isOpen={this.state.closeWithoutSaving}
            onClose={this.toggleCloseWithoutSaving}
            title="You have unsaved changes on this page"
            body="If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?"
            confirm="LEAVE PAGE"
            action={() => {
              this.setState(
                { editMade: false, closeWithoutSaving: false },
                this.handleCloseDrawer
              );
            }}
          />
        </ScrollWrapper>
      </Drawer>
    );
  }
}

export default PrescreenEditQuestionDrawer;
